/**
 * A Photoshop file, read in the browser.
 *
 * Layers keep their names, their places and their order; text stays text where
 * the shop has the font, and everything else becomes a picture of itself. That
 * last part is deliberate: a smart object, an adjustment layer, a drop shadow
 * and a blend mode cannot be reproduced by this editor's own drawing, and a
 * layer that arrives looking almost right is worse than one that arrives as a
 * picture and says so.
 *
 * CMYK files are refused. The library that reads PSDs handles RGB only, and
 * most print artwork is CMYK - so the message says exactly which two menu
 * items in Photoshop fix it, rather than "unsupported colour mode".
 */

import { ImportProblem, PRINT_DPI, mm, pxToMm, type ImportDoc, type ImportPiece } from './types';

/** Layers past this and a template stops being a template. */
const MOST_LAYERS = 120;

/*
 * The library's own types, imported as types only: the library itself is
 * fetched when a PSD is opened, and a type import leaves nothing in the build.
 */
import type { Layer as PsdLayer } from 'ag-psd';

/**
 * Reads a PSD.
 *
 * @param file       The file the shop chose.
 * @param haveFont   Whether the shop has a font, by family name.
 */
export async function readPsd( file: File, haveFont: ( family: string ) => string | null ): Promise<ImportDoc> {
  // Loaded only when a PSD is actually opened: it is a large library, and no
  // customer's editor should ever download it.
  const { readPsd: read } = await import( 'ag-psd' );
  const bytes = await file.arrayBuffer();

  let psd;

  try {
    psd = read( bytes, { skipThumbnail: true, useImageData: false } );
  } catch ( error ) {
    const said = error instanceof Error ? error.message : String( error );

    if ( /CMYK|color mode/i.test( said ) ) {
      throw new ImportProblem(
        'This file is in CMYK. In Photoshop choose Image ▸ Mode ▸ RGB Color, save a copy, and import that instead.'
      );
    }

    throw new ImportProblem( `That Photoshop file could not be read (${ said }).` );
  }

  const dpi = Math.round( psd.imageResources?.resolutionInfo?.horizontalResolution ?? 72 ) || 72;
  const notes: string[] = [];
  const missing = new Set<string>();
  const flattened: string[] = [];
  let seen = 0;

  const piece = ( layer: PsdLayer ): ImportPiece | null => {
    if ( seen >= MOST_LAYERS ) return null;

    seen++;

    const left = layer.left ?? 0;
    const top = layer.top ?? 0;
    const box = {
      x: mm( pxToMm( left, dpi ) ),
      y: mm( pxToMm( top, dpi ) ),
      w: mm( pxToMm( ( layer.right ?? 0 ) - left, dpi ) ),
      h: mm( pxToMm( ( layer.bottom ?? 0 ) - top, dpi ) ),
    };

    const common = {
      name: layer.name || 'Layer',
      hidden: layer.hidden === true,
      opacity: typeof layer.opacity === 'number' ? layer.opacity : 1,
      box,
    };

    if ( layer.children ) {
      const children = layer.children.map( piece ).filter( Boolean ) as ImportPiece[];

      if ( ! children.length ) return null;

      // A group's own box is not recorded in a PSD; it is whatever its
      // children cover.
      const x = Math.min( ...children.map( ( c ) => c.box.x ) );
      const y = Math.min( ...children.map( ( c ) => c.box.y ) );

      return {
        ...common,
        kind: 'group',
        box: {
          x,
          y,
          w: Math.max( ...children.map( ( c ) => c.box.x + c.box.w ) ) - x,
          h: Math.max( ...children.map( ( c ) => c.box.y + c.box.h ) ) - y,
        },
        children,
      };
    }

    /*
     * Text, where the font is one the shop has.
     *
     * Where it is not, the layer still comes in - as the picture Photoshop
     * drew - because a template missing its headline is worse than a template
     * whose headline cannot be retyped. The font is named in the notes so the
     * shop can add it and import again if it matters.
     */
    if ( layer.text?.text ) {
      const asked = layer.text.style?.font?.name ?? '';
      const family = haveFont( asked );

      if ( family ) {
        const fill = layer.text.style?.fillColor as { r?: number; g?: number; b?: number } | undefined;
        const justify = String( layer.text.paragraphStyle?.justification ?? 'left' );

        return {
          ...common,
          kind: 'text',
          text: {
            words: layer.text.text.replace( /\r/g, '\n' ),
            family,
            /*
             * Photoshop measures type in the document's own pixels, so a
             * "48" in a 300 dpi file is 48 pixels tall, not 48 points. Read as
             * points it arrived four times too big, straight off the card.
             */
            size: Math.max( 4, Math.round( ( ( layer.text.style?.fontSize ?? 12 ) * 72 ) / dpi ) ),
            colour:
              fill && typeof fill.r === 'number'
                ? `#${ [ fill.r, fill.g ?? 0, fill.b ?? 0 ].map( ( c ) => Math.round( c ).toString( 16 ).padStart( 2, '0' ) ).join( '' ) }`
                : '#000000',
            align: justify.startsWith( 'center' ) ? 'center' : justify.startsWith( 'right' ) ? 'right' : 'left',
            // Photoshop's own faux styles; a font that is genuinely bold
            // carries it in its name, which the font match above handles.
            bold: !! layer.text.style?.fauxBold,
            italic: !! layer.text.style?.fauxItalic,
          },
        };
      }

      if ( asked ) missing.add( asked );
    }

    if ( ! layer.canvas || box.w <= 0 || box.h <= 0 ) return null;

    if ( layer.placedLayer || layer.effects || ( layer.blendMode && layer.blendMode !== 'normal' ) ) {
      flattened.push( common.name );
    }

    return { ...common, kind: 'picture', canvas: downsample( layer.canvas as HTMLCanvasElement, box.w, box.h ) };
  };

  const pieces = ( psd.children ?? [] ).map( piece ).filter( Boolean ) as ImportPiece[];

  if ( ! pieces.length ) {
    throw new ImportProblem( 'There are no layers in that file to import.' );
  }

  if ( seen >= MOST_LAYERS ) {
    notes.push( `Only the first ${ MOST_LAYERS } layers were read. Flatten what you can in Photoshop and import again.` );
  }

  if ( missing.size ) {
    notes.push(
      `These fonts are not in your library, so those layers came in as pictures: ${ [ ...missing ].join( ', ' ) }.`
    );
  }

  if ( flattened.length ) {
    notes.push(
      `Smart objects, effects and blend modes cannot be redrawn here, so these came in as pictures: ${ flattened.slice( 0, 6 ).join( ', ' ) }${ flattened.length > 6 ? ` and ${ flattened.length - 6 } more` : '' }.`
    );
  }

  return {
    source: 'psd',
    width: mm( pxToMm( psd.width, dpi ) ),
    height: mm( pxToMm( psd.height, dpi ) ),
    pieces,
    notes,
  };
}

/**
 * A layer's pixels, no finer than the press can print them.
 *
 * Photoshop files are often built at 600 dpi or at sizes nobody will print;
 * carrying that to the server would be a 30 MB upload for a logo. Anything
 * already coarser than 300 dpi is left exactly as it is.
 */
function downsample( canvas: HTMLCanvasElement, widthMm: number, heightMm: number ): HTMLCanvasElement {
  const most = Math.max( 1, Math.round( ( widthMm / 25.4 ) * PRINT_DPI ) );

  if ( canvas.width <= most ) return canvas;

  const scaled = document.createElement( 'canvas' );

  scaled.width = most;
  scaled.height = Math.max( 1, Math.round( ( heightMm / 25.4 ) * PRINT_DPI ) );

  const pen = scaled.getContext( '2d' );

  if ( pen ) {
    pen.imageSmoothingQuality = 'high';
    pen.drawImage( canvas, 0, 0, scaled.width, scaled.height );
  }

  return scaled;
}
